import React, { useRef, useEffect, useState } from 'react';
import * as d3 from 'd3';

const CHART_HEIGHT = 300;
const MARGIN = { top: 20, right: 80, bottom: 50, left: 60 };

const TREND_COLORS = {
  converging: '#16a34a',
  diverging: '#dc2626',
  stable: '#6b7280',
};

const TREND_BADGES = {
  converging: { bg: '#dcfce7', color: '#166534', label: 'CONVERGING', arrow: '\u2193' },
  diverging: { bg: '#fee2e2', color: '#991b1b', label: 'DIVERGING', arrow: '\u2191' },
  stable: { bg: '#f3f4f6', color: '#6b7280', label: 'STABLE', arrow: '\u2192' },
};

const styles = {
  card: {
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '8px',
    padding: '24px',
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
  headerRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '4px',
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    color: '#1f2937',
  },
  badge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    padding: '2px 8px',
    borderRadius: '9999px',
    fontSize: '11px',
    fontWeight: 600,
    letterSpacing: '0.025em',
  },
  subtitle: {
    fontSize: '12px',
    color: '#9ca3af',
    marginBottom: '12px',
  },
  legend: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '16px',
    marginBottom: '12px',
    fontSize: '12px',
    color: '#6b7280',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  container: {
    overflowX: 'auto',
  },
  tooltip: {
    position: 'fixed',
    pointerEvents: 'none',
    background: '#fff',
    border: '1px solid #e5e7eb',
    borderRadius: '6px',
    padding: '8px 12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.12)',
    fontSize: '12px',
    zIndex: 1000,
    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
  },
};

function formatDateLabel(dateStr) {
  const date = new Date(dateStr);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[date.getMonth()]} ${date.getDate()}`;
}

function formatDateFull(dateStr) {
  const date = new Date(dateStr);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${months[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
}

function linearRegression(values) {
  const n = values.length;
  if (n < 2) return null;

  let sumX = 0;
  let sumY = 0;
  let sumXY = 0;
  let sumX2 = 0;

  for (let i = 0; i < n; i++) {
    sumX += i;
    sumY += values[i];
    sumXY += i * values[i];
    sumX2 += i * i;
  }

  const slope = (n * sumXY - sumX * sumY) / (n * sumX2 - sumX * sumX);
  const intercept = (sumY - slope * sumX) / n;

  return { slope, intercept };
}

export default function ConvergenceChart({ data }) {
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const [tooltip, setTooltip] = useState(null);
  const [resizeCount, setResizeCount] = useState(0);

  useEffect(() => {
    if (!data || !data.dates || data.dates.length === 0) return;

    const rafId = requestAnimationFrame(() => {
      const svg = d3.select(svgRef.current);
      svg.selectAll('*').remove();

      const containerWidth = containerRef.current
        ? containerRef.current.clientWidth
        : 700;
      const width = Math.max(600, containerWidth);
      const innerW = width - MARGIN.left - MARGIN.right;
      const innerH = CHART_HEIGHT - MARGIN.top - MARGIN.bottom;

      svg.attr('width', width).attr('height', CHART_HEIGHT);

      const g = svg.append('g').attr('transform', `translate(${MARGIN.left}, ${MARGIN.top})`);

      const n = data.dates.length;

      // -- Scales --

      // X scale: time-based using date strings
      const parsedDates = data.dates.map((d) => new Date(d));
      const x = d3.scaleTime()
        .domain(d3.extent(parsedDates))
        .range([0, innerW]);

      // Left Y axis: dollars (stdDev, IQR, range)
      const dollarValues = [
        ...data.stdDev,
        ...data.iqr,
        ...data.range,
      ].filter((v) => v != null);
      const dollarMax = d3.max(dollarValues) || 1;
      const dollarPadding = dollarMax * 0.1 || 0.5;

      const yLeft = d3.scaleLinear()
        .domain([0, dollarMax + dollarPadding])
        .nice()
        .range([innerH, 0]);

      // Right Y axis: percentage (CV%)
      const cvValues = data.cv.filter((v) => v != null);
      const cvMax = d3.max(cvValues) || 10;
      const cvPadding = cvMax * 0.1 || 1;

      const yRight = d3.scaleLinear()
        .domain([0, cvMax + cvPadding])
        .nice()
        .range([innerH, 0]);

      // -- Grid lines --
      const yTicks = yLeft.ticks(6);
      g.selectAll('.grid-line')
        .data(yTicks)
        .enter()
        .append('line')
        .attr('x1', 0)
        .attr('x2', innerW)
        .attr('y1', (d) => yLeft(d))
        .attr('y2', (d) => yLeft(d))
        .attr('stroke', '#f3f4f6')
        .attr('stroke-width', 1);

      // -- Range background band --
      const rangeArea = d3.area()
        .x((d, i) => x(parsedDates[i]))
        .y0(innerH)
        .y1((d) => yLeft(d))
        .defined((d) => d != null)
        .curve(d3.curveMonotoneX);

      g.append('path')
        .datum(data.range)
        .attr('d', rangeArea)
        .attr('fill', '#f3f4f6')
        .attr('stroke', 'none');

      // -- IQR area fill --
      const iqrArea = d3.area()
        .x((d, i) => x(parsedDates[i]))
        .y0(innerH)
        .y1((d) => yLeft(d))
        .defined((d) => d != null)
        .curve(d3.curveMonotoneX);

      g.append('path')
        .datum(data.iqr)
        .attr('d', iqrArea)
        .attr('fill', '#2563eb')
        .attr('fill-opacity', 0.1)
        .attr('stroke', 'none');

      // -- Std Dev line --
      const stdDevLine = d3.line()
        .x((d, i) => x(parsedDates[i]))
        .y((d) => yLeft(d))
        .defined((d) => d != null)
        .curve(d3.curveMonotoneX);

      g.append('path')
        .datum(data.stdDev)
        .attr('d', stdDevLine)
        .attr('fill', 'none')
        .attr('stroke', '#2563eb')
        .attr('stroke-width', 2);

      // -- CV% line (right Y axis, dashed) --
      const cvLine = d3.line()
        .x((d, i) => x(parsedDates[i]))
        .y((d) => yRight(d))
        .defined((d) => d != null)
        .curve(d3.curveMonotoneX);

      g.append('path')
        .datum(data.cv)
        .attr('d', cvLine)
        .attr('fill', 'none')
        .attr('stroke', '#8b5cf6')
        .attr('stroke-width', 2)
        .attr('stroke-dasharray', '4,3');

      // -- Trend line (linear regression through stdDev) --
      const reg = linearRegression(data.stdDev);
      if (reg) {
        const trendColor = TREND_COLORS[data.trend] || TREND_COLORS.stable;
        // Extend one step beyond data in each direction
        const extStart = -0.5;
        const extEnd = n - 0.5;

        const trendStartY = reg.intercept + reg.slope * extStart;
        const trendEndY = reg.intercept + reg.slope * extEnd;

        // Map the extended indices to x positions
        const dateRange = parsedDates[n - 1].getTime() - parsedDates[0].getTime();
        const stepSize = n > 1 ? dateRange / (n - 1) : 0;

        const trendStartX = x(new Date(parsedDates[0].getTime() + stepSize * extStart));
        const trendEndX = x(new Date(parsedDates[0].getTime() + stepSize * extEnd));

        // Clamp to chart bounds
        const clampX = (v) => Math.max(0, Math.min(innerW, v));
        const clampY = (v) => Math.max(0, Math.min(innerH, v));

        g.append('line')
          .attr('x1', clampX(trendStartX))
          .attr('y1', clampY(yLeft(trendStartY)))
          .attr('x2', clampX(trendEndX))
          .attr('y2', clampY(yLeft(trendEndY)))
          .attr('stroke', trendColor)
          .attr('stroke-width', 2)
          .attr('stroke-dasharray', '6,4');
      }

      // -- Axes --

      // X axis
      g.append('g')
        .attr('transform', `translate(0, ${innerH})`)
        .call(
          d3.axisBottom(x)
            .ticks(Math.min(n, 10))
            .tickSize(0)
            .tickFormat((d) => formatDateLabel(d.toISOString()))
        )
        .call((axis) => axis.select('.domain').attr('stroke', '#e5e7eb'))
        .selectAll('text')
        .attr('font-size', '10px')
        .attr('fill', '#6b7280')
        .attr('transform', 'rotate(-30)')
        .attr('text-anchor', 'end')
        .attr('dx', '-0.5em')
        .attr('dy', '0.25em');

      // Left Y axis (dollars)
      g.append('g')
        .call(
          d3.axisLeft(yLeft)
            .ticks(6)
            .tickSize(0)
            .tickFormat((v) => `$${v.toFixed(2)}`)
        )
        .call((axis) => axis.select('.domain').attr('stroke', '#e5e7eb'))
        .selectAll('text')
        .attr('font-size', '10px')
        .attr('fill', '#6b7280');

      // Right Y axis (CV%)
      g.append('g')
        .attr('transform', `translate(${innerW}, 0)`)
        .call(
          d3.axisRight(yRight)
            .ticks(6)
            .tickSize(0)
            .tickFormat((v) => `${v.toFixed(1)}%`)
        )
        .call((axis) => axis.select('.domain').attr('stroke', '#e5e7eb'))
        .selectAll('text')
        .attr('font-size', '10px')
        .attr('fill', '#8b5cf6');

      // -- Axis labels --
      g.append('text')
        .attr('transform', 'rotate(-90)')
        .attr('x', -innerH / 2)
        .attr('y', -45)
        .attr('text-anchor', 'middle')
        .attr('font-size', '10px')
        .attr('fill', '#6b7280')
        .text('Spread ($)');

      g.append('text')
        .attr('transform', 'rotate(90)')
        .attr('x', innerH / 2)
        .attr('y', -innerW - 55)
        .attr('text-anchor', 'middle')
        .attr('font-size', '10px')
        .attr('fill', '#8b5cf6')
        .text('CV (%)');

      // -- Tooltip hover overlay --
      // Vertical crosshair line (hidden by default)
      const crosshair = g.append('line')
        .attr('y1', 0)
        .attr('y2', innerH)
        .attr('stroke', '#d1d5db')
        .attr('stroke-width', 1)
        .attr('stroke-dasharray', '3,3')
        .style('display', 'none');

      // Invisible overlay for mouse tracking
      g.append('rect')
        .attr('width', innerW)
        .attr('height', innerH)
        .attr('fill', 'transparent')
        .style('cursor', 'crosshair')
        .on('mousemove', (event) => {
          const [mx] = d3.pointer(event);
          const bisect = d3.bisector((d) => d).left;
          const dateAtMouse = x.invert(mx);
          const timestamps = parsedDates.map((d) => d.getTime());
          let idx = bisect(timestamps, dateAtMouse.getTime());
          // Snap to nearest point
          if (idx > 0 && idx < n) {
            const d0 = timestamps[idx - 1];
            const d1 = timestamps[idx];
            idx = (dateAtMouse.getTime() - d0) > (d1 - dateAtMouse.getTime()) ? idx : idx - 1;
          }
          idx = Math.max(0, Math.min(n - 1, idx));

          const cx = x(parsedDates[idx]);
          crosshair
            .attr('x1', cx)
            .attr('x2', cx)
            .style('display', null);

          setTooltip({
            x: event.clientX + 12,
            y: event.clientY - 10,
            date: data.dates[idx],
            stdDev: data.stdDev[idx],
            iqr: data.iqr[idx],
            range: data.range[idx],
            cv: data.cv[idx],
          });
        })
        .on('mouseleave', () => {
          crosshair.style('display', 'none');
          setTooltip(null);
        });
    });

    return () => {
      cancelAnimationFrame(rafId);
      d3.select(svgRef.current).selectAll('*').remove();
    };
  }, [data, resizeCount]);

  // Handle window resize — trigger redraw by bumping a counter
  useEffect(() => {
    let resizeTimer;
    const debouncedResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        setResizeCount((c) => c + 1);
      }, 150);
    };

    window.addEventListener('resize', debouncedResize);
    return () => {
      clearTimeout(resizeTimer);
      window.removeEventListener('resize', debouncedResize);
    };
  }, []);

  // Empty state
  if (!data || !data.dates || data.dates.length === 0) {
    return (
      <div style={styles.card}>
        <div style={styles.headerRow}>
          <div style={styles.title}>Price Convergence Analysis</div>
        </div>
        <div style={styles.subtitle}>Are prices becoming more or less uniform over time?</div>
        <div style={{ color: '#6b7280', textAlign: 'center', padding: '40px 0' }}>
          Not enough data for convergence analysis
        </div>
      </div>
    );
  }

  const badge = TREND_BADGES[data.trend] || TREND_BADGES.stable;
  const trendColor = TREND_COLORS[data.trend] || TREND_COLORS.stable;

  return (
    <div style={styles.card}>
      <div style={styles.headerRow}>
        <div style={styles.title}>Price Convergence Analysis</div>
        <span
          style={{
            ...styles.badge,
            background: badge.bg,
            color: badge.color,
          }}
        >
          {badge.label} {badge.arrow}
        </span>
        {data.trendStrength != null && (
          <span style={{ fontSize: '11px', color: '#9ca3af' }}>
            ({(data.trendStrength * 100).toFixed(0)}% strength)
          </span>
        )}
      </div>
      <div style={styles.subtitle}>Are prices becoming more or less uniform over time?</div>

      {/* Legend */}
      <div style={styles.legend}>
        <div style={styles.legendItem}>
          <svg width="20" height="10">
            <line x1="0" y1="5" x2="20" y2="5" stroke="#2563eb" strokeWidth="2" />
          </svg>
          <span>Std Dev ($)</span>
        </div>
        <div style={styles.legendItem}>
          <svg width="20" height="10">
            <rect x="0" y="0" width="20" height="10" fill="#2563eb" fillOpacity="0.1" rx="2" />
          </svg>
          <span>IQR</span>
        </div>
        <div style={styles.legendItem}>
          <svg width="20" height="10">
            <line x1="0" y1="5" x2="20" y2="5" stroke="#8b5cf6" strokeWidth="2" strokeDasharray="4,3" />
          </svg>
          <span>CV (%)</span>
        </div>
        <div style={styles.legendItem}>
          <svg width="20" height="10">
            <line x1="0" y1="5" x2="20" y2="5" stroke={trendColor} strokeWidth="2" strokeDasharray="6,4" />
          </svg>
          <span>Trend</span>
        </div>
      </div>

      <div style={styles.container} ref={containerRef}>
        <svg ref={svgRef} />
      </div>

      {/* Tooltip */}
      {tooltip && (
        <div
          style={{
            ...styles.tooltip,
            left: tooltip.x,
            top: tooltip.y,
          }}
        >
          <div style={{ fontWeight: 600, color: '#1f2937', marginBottom: '4px' }}>
            {formatDateFull(tooltip.date)}
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
            <span style={{ color: '#6b7280' }}>Std Dev</span>
            <span style={{ fontWeight: 600, color: '#2563eb' }}>
              ${tooltip.stdDev != null ? tooltip.stdDev.toFixed(2) : '--'}
            </span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
            <span style={{ color: '#6b7280' }}>IQR</span>
            <span style={{ fontWeight: 600, color: '#2563eb' }}>
              ${tooltip.iqr != null ? tooltip.iqr.toFixed(2) : '--'}
            </span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px', marginBottom: '2px' }}>
            <span style={{ color: '#6b7280' }}>Range</span>
            <span style={{ fontWeight: 600 }}>
              ${tooltip.range != null ? tooltip.range.toFixed(2) : '--'}
            </span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
            <span style={{ color: '#8b5cf6' }}>CV</span>
            <span style={{ fontWeight: 600, color: '#8b5cf6' }}>
              {tooltip.cv != null ? tooltip.cv.toFixed(1) : '--'}%
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
